import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, MapPin, Loader2 } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import OwnerLayout from "@/components/owner/OwnerLayout";
import PropertyStatusBadge, { PROPERTY_TYPE_LABELS, GENDER_LABELS, PROPERTY_STATUS_LABELS } from "@/components/property/PropertyStatusBadge";
import NearbyAuto from "@/components/property/NearbyAuto";
import AmenityDisplay from "@/components/property/AmenityDisplay";
import RoomsManager from "@/components/property/RoomsManager";
import PhotoManager from "@/components/property/PhotoManager";
import ListingPanel from "@/components/property/ListingPanel";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value || "-"}</span>
    </div>
  );
}

export default function PropertyViewPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProperty = () =>
    apiClient.get(`/properties/${id}`)
      .then((r) => setP(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    loadProperty();
    apiClient.get(`/properties/${id}/amenities`)
      .then((r) => setAmenities(r.data || []))
      .catch(() => {});
  }, [id]);

  return (
    <OwnerLayout testid="property-view-page">
      <Link to="/owner/properties" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-4" data-testid="view-back-link">
        <ArrowLeft size={16} /> Property Saya
      </Link>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="view-error">{error}</div>
      ) : (
        <div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="view-property-name">{p.name}</h1>
                <PropertyStatusBadge status={p.status} testid="view-property-status" />
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin size={15} /> {p.location?.address}, {p.location?.subdistrict_name}, {p.location?.district_name}, {p.location?.city_name}, {p.location?.province_name}
              </p>
            </div>
            <Link to={`/owner/properties/${p.id}/edit`} data-testid="view-edit-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform">
              <Pencil size={16} /> Edit
            </Link>
          </div>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="view-photos-section">
            <PhotoManager propertyId={p.id} title="Foto Property" />
          </section>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <section className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Informasi</h3>
              <Row label="Tipe Property" value={PROPERTY_TYPE_LABELS[p.property_type]} />
              <Row label="Gender" value={GENDER_LABELS[p.gender_type]} />
              <Row label="Status" value={PROPERTY_STATUS_LABELS[p.status] || p.status} />
              <div className="pt-3">
                <p className="text-sm text-muted-foreground">Deskripsi</p>
                <p className="mt-1 text-sm text-foreground whitespace-pre-line">{p.description}</p>
              </div>
            </section>
            <section className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Lokasi & Koordinat</h3>
              <Row label="Provinsi" value={p.location?.province_name} />
              <Row label="Kota/Kabupaten" value={p.location?.city_name} />
              <Row label="Kecamatan" value={p.location?.district_name} />
              <Row label="Kelurahan/Desa" value={p.location?.subdistrict_name} />
              <Row label="Alamat" value={p.location?.address} />
              <Row label="Kode Pos" value={p.location?.postal_code} />
              <Row label="Latitude" value={p.latitude} />
              <Row label="Longitude" value={p.longitude} />
            </section>
          </div>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="view-rooms-section">
            <RoomsManager propertyId={p.id} />
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="view-amenities-section">
            <h3 className="font-heading text-lg font-semibold mb-3">Fasilitas Property</h3>
            <AmenityDisplay amenities={amenities} testid="view-amenity-display" />
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="view-nearby-section">
            <h3 className="font-heading text-lg font-semibold mb-3">Tempat di Sekitar</h3>
            <NearbyAuto propertyId={p.id} />
          </section>

          <section className="mt-6 bg-card border border-border rounded-2xl p-6" data-testid="view-listing-section">
            <ListingPanel property={p} onChanged={loadProperty} />
          </section>
        </div>
      )}
    </OwnerLayout>
  );
}
